import { SubscriptionInvoice } from "../../../types/billing";
import { UNPAID_INVOICE_STATUSES } from "./unpaidInvoiceSettlement";

export const DUE_SUBSCRIPTION_STATUSES = ["past_due", "unpaid"] as const;

const OPEN_OR_UNPAID_INVOICE_STATUSES = [...UNPAID_INVOICE_STATUSES, "unpaid"];

export type PaymentMethodPromptReason =
  | "no_payment_method"
  | "subscription_due"
  | "open_or_unpaid_invoice";

export function getPaymentMethodPromptDismissedKey(orgId: string) {
  return `payment-method-prompt-dismissed-${orgId}`;
}

export const NON_SUBSCRIPTION_MANAGER_BILLING_MESSAGE =
  "Billing setup requires attention for this organization. Please contact your Subscription Manager for assistance.";

export function isNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  const status = (error as { response?: { status?: number } }).response?.status;
  return status === 404;
}

export function isSubscriptionDue(status?: string | null): boolean {
  return (
    !!status &&
    (DUE_SUBSCRIPTION_STATUSES as readonly string[]).includes(status)
  );
}

export function isOpenOrUnpaidInvoice(invoice: SubscriptionInvoice): boolean {
  const status = invoice.status?.toLowerCase();
  if (!status || !OPEN_OR_UNPAID_INVOICE_STATUSES.includes(status)) {
    return false;
  }
  return (invoice.amountDue || invoice.totalAmountCents || 0) > 0;
}

export function getPaymentMethodPromptReasons(input: {
  paymentMethods?: readonly unknown[] | null;
  paymentMethodsKnown: boolean;
  subscriptionStatus?: string | null;
  invoices?: readonly SubscriptionInvoice[] | null;
}): PaymentMethodPromptReason[] {
  const hasNoPaymentMethod =
    input.paymentMethodsKnown && (input.paymentMethods?.length ?? 0) === 0;
  // A saved card can already pay a due subscription or an open invoice.
  if (!hasNoPaymentMethod) {
    return [];
  }

  const reasons: PaymentMethodPromptReason[] = ["no_payment_method"];
  if (isSubscriptionDue(input.subscriptionStatus)) {
    reasons.push("subscription_due");
  }
  if ((input.invoices ?? []).some(isOpenOrUnpaidInvoice)) {
    reasons.push("open_or_unpaid_invoice");
  }
  return reasons;
}

export function formatPaymentMethodPromptMessage(
  reasons: PaymentMethodPromptReason[],
): string {
  const clauses: string[] = [];
  if (reasons.includes("no_payment_method")) {
    clauses.push("there is no payment method on file");
  }
  if (reasons.includes("subscription_due")) {
    clauses.push("your subscription payment is due");
  }
  if (reasons.includes("open_or_unpaid_invoice")) {
    clauses.push("you have an open or unpaid invoice");
  }
  if (clauses.length === 0) {
    return "";
  }

  const sentence =
    clauses.length === 1
      ? clauses[0]
      : `${clauses.slice(0, -1).join(", ")}, and ${clauses[clauses.length - 1]}`;
  const lead = sentence.charAt(0).toUpperCase() + sentence.slice(1);
  return `${lead}. Please add a payment method to keep your subscription active and payments up to date.`;
}
